import { sharePointService } from './sharePointService';
import { emailService } from './emailService';
import { getContentType } from '@/config';
import { logger } from '@/utils/logger';

/**
 * Reference to a message that can be resolved into bytes + filename.
 * Covers drag-and-drop files, the currently-open email, and multi-select items.
 */
export type MessageRef =
  | { kind: 'file'; file: File }
  | { kind: 'itemId'; itemId: string; subject: string; date?: Date };

/**
 * Shape of a single item delivered by the Office DragAndDropEvent
 * (Office.DroppedItemDetails). A dragged message arrives as one .eml.
 */
export interface DroppedItem {
  fileContent: Blob;
  name: string;
  type?: string;
}

/**
 * Convert Office drop payload items into browser File objects. Dropped items are
 * Office.DroppedItemDetails ({ fileContent: Blob, name, type }), NOT File objects,
 * so each is wrapped (preserving name + type) to flow through the same upload
 * pipeline (and progress UI) as desktop-file drops. A message arrives as one .eml.
 */
export function droppedItemsToFiles(items: DroppedItem[]): File[] {
  return items.map((d) => new File([d.fileContent], d.name, { type: d.type || '' }));
}

/** Progress callback fired once per message as it completes. */
export type SaveProgressCallback = (completed: number, total: number, fileName: string) => void;

/** Result of saving a single message. */
export interface SaveMessageResult {
  fileName: string;
  status: 'saved' | 'duplicate' | 'error';
  error?: string;
}

/**
 * Derive MIME type from filename extension.
 * Outlook on the web sometimes omits the MIME type on dropped .eml files.
 */
function deriveMimeType(fileName: string): string {
  return getContentType(fileName);
}

/**
 * Resolve a MessageRef into { blob, fileName, contentHash } ready for upload.
 */
async function resolveRef(ref: MessageRef): Promise<{
  blob: Blob;
  fileName: string;
  contentHash: string;
  receivedDate?: Date;
  hasAttachments?: boolean;
}> {
  switch (ref.kind) {
    case 'file': {
      const { file } = ref;
      const arrayBuffer = await file.arrayBuffer();
      const contentHash = await sharePointService.generateContentHash(arrayBuffer);
      // Handle missing MIME type (Outlook Web drops .eml without type)
      const mimeType = file.type || deriveMimeType(file.name);
      const blob = mimeType !== file.type ? new Blob([arrayBuffer], { type: mimeType }) : file;
      let receivedDate: Date | undefined;
      try {
        receivedDate = await emailService.getReceivedDateFromFile(file.name, arrayBuffer);
      } catch (error) {
        logger.warn(
          'Received date unavailable; saving without it:',
          error instanceof Error ? error.name : 'unknown parser error',
        );
      }
      const extension = file.name.toLowerCase().split('.').pop();
      let hasAttachments: boolean | undefined;
      if (extension === 'eml' || extension === 'msg') {
        try {
          hasAttachments = await emailService.detectEmailAttachments(arrayBuffer, extension);
        } catch (error) {
          logger.warn(
            'Attachment metadata unavailable; saving without HasAttachments:',
            error instanceof Error ? error.name : 'unknown parser error',
          );
        }
      }
      return { blob, fileName: file.name, contentHash, receivedDate, hasAttachments };
    }

    case 'itemId': {
      // Lossless path: full MIME (attachments included) via Graph
      // /messages/{id}/$value with the NAA token.
      const { content, fileName, contentHash, receivedDate } =
        await emailService.getEmailAsEmlByItemId(ref.itemId, ref.subject, ref.date);
      const blob = new Blob([content], { type: 'message/rfc822' });
      let hasAttachments: boolean | undefined;
      try {
        const data = new TextEncoder().encode(content).buffer as ArrayBuffer;
        hasAttachments = await emailService.detectEmailAttachments(data, 'eml');
      } catch (error) {
        logger.warn(
          'Attachment metadata unavailable; saving without HasAttachments:',
          error instanceof Error ? error.name : 'unknown parser error',
        );
      }
      return { blob, fileName, contentHash, receivedDate, hasAttachments };
    }
  }
}

/**
 * Core save function. Resolves each MessageRef, uploads via sharePointService,
 * and reports progress. All entry points (drop handler, ribbon button,
 * multi-select) route through here.
 */
export async function saveMessages(
  refs: MessageRef[],
  destPath: string,
  onProgress?: SaveProgressCallback,
): Promise<SaveMessageResult[]> {
  const results: SaveMessageResult[] = [];
  let completed = 0;

  for (const ref of refs) {
    let fileName = '(unknown)';
    try {
      const resolved = await resolveRef(ref);
      fileName = resolved.fileName;

      try {
        const displayName = await sharePointService.uploadFile(
          resolved.blob,
          destPath,
          resolved.fileName,
          resolved.contentHash,
          undefined,
          undefined,
          resolved.receivedDate,
          resolved.hasAttachments,
        );
        results.push({ fileName: displayName, status: 'saved' });
      } catch (uploadError) {
        if ((uploadError as Error).message === 'DUPLICATE_FILE') {
          results.push({ fileName, status: 'duplicate' });
        } else {
          throw uploadError;
        }
      }
    } catch (error) {
      results.push({
        fileName,
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }

    completed++;
    onProgress?.(completed, refs.length, fileName);
  }

  return results;
}

/**
 * Read selected items from multi-select API (getSelectedItemsAsync).
 * Returns MessageRefs for up to 100 items (API cap).
 */
export async function getMultiSelectRefs(): Promise<MessageRef[]> {
  if (typeof Office === 'undefined' || !Office.context?.mailbox) {
    throw new Error('Office mailbox not available');
  }

  const mailbox = Office.context.mailbox as any;
  if (typeof mailbox.getSelectedItemsAsync !== 'function') {
    throw new Error('Multi-select not supported in this Outlook version. Requires Mailbox 1.13.');
  }

  return new Promise<MessageRef[]>((resolve, reject) => {
    mailbox.getSelectedItemsAsync((result: Office.AsyncResult<any[]>) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error(result.error?.message || 'Failed to get selected items'));
        return;
      }

      const items: any[] = result.value || [];
      // API cap: 100 items
      const capped = items.slice(0, 100);

      const refs: MessageRef[] = capped.map((item: any) => ({
        kind: 'itemId' as const,
        itemId: item.itemId,
        subject: item.subject || '(No Subject)',
        date: item.receivedDateTime
          ? new Date(item.receivedDateTime)
          : item.dateTimeCreated
            ? new Date(item.dateTimeCreated)
            : undefined,
      }));

      resolve(refs);
    });
  });
}
